import { useEffect, useState } from 'react';

import {
  Button,
  InputLabel,
  Input,
  FormLabel,
  FormControl,
  FormGroup,
  FormControlLabel,
  FormHelperText,
  Grid,
  Checkbox,
  Select,
  MenuItem,
} from '@mui/material';

import { HistorySettingsForm } from './types';
import { Snapshot } from 'types';
import { useIsSnapshotLocked } from '../analyzers/hooks/useIsSnapshotLocked';

import { useSnapshot } from 'features/snapshots/api/getSnapshot';
import { useSnapshotHistorySettings } from './api/getSnapshotHistorySettings';
import { useCreateSnapshotHistorySettings } from './api/createSnapshotHistorySettings';
import { useUpdateSnapshotHistorySettings } from './api/updateSnapshotHistorySettings';
import { useUpdateMultipleSnapshotHistorySettings } from './api/updateMultipleSnapshotHistorySettings';
import { Controller, useForm } from 'react-hook-form';
import { useIsAdmin } from 'features/auth/hooks/useIsAdmin';
import { LoadingButton } from '@mui/lab';
import { useSnapshotLockers } from './hooks/useSnapshotLockers';
import { getSubmitAllIcon, isSubmitAllDisabled } from './utils/statusCheckers';

const defaultHistorySettings: HistorySettingsForm = {
  continuous_mode: false,
  continuous_mode_end_time: undefined,
  interval_mode: false,
  interference_mode: false,
  record_interval: 20,
  polling_continuous_mode: false,
  polling_interference_mode: false,
  polling_record_timer_enabled: false,
  polling_record_timer_value: 10,
  polling_record_timer_unit: 'minutes',
  recording_locked: false,
};

interface Props {
  snapshots: Snapshot[];
  selectedSnapshotId: number;
}

const SnapshotHistorySettingsPicker = ({
  snapshots,
  selectedSnapshotId,
}: Props) => {
  const { data: snapshot } = useSnapshot({ snapshotId: selectedSnapshotId });
  const { data: historySettings } = useSnapshotHistorySettings({
    snapshotId: selectedSnapshotId,
  });
  const [appliedToAll, setAppliedToAll] = useState<number>(-1);
  const {
    getValues,
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isDirty },
  } = useForm<HistorySettingsForm>({
    defaultValues: defaultHistorySettings,
  });

  const createSnapshotHistorySettingsMutation =
    useCreateSnapshotHistorySettings({});
  const updateSnapshotHistorySettingsMutation =
    useUpdateSnapshotHistorySettings({
      snapshotId: selectedSnapshotId,
    });
  const updateMultipleHistorySettingsMutation =
    useUpdateMultipleSnapshotHistorySettings({});
  const isAdmin = useIsAdmin();

  const isSnapshotLocked =
    useIsSnapshotLocked(snapshot) || watch('recording_locked');

  const { updateLock, updateAllLocks } = useSnapshotLockers(snapshots);

  useEffect(() => {
    const initialSettings =
      !historySettings || snapshot === undefined
        ? {
            ...defaultHistorySettings,
            recording_locked: !!snapshot?.recording_locked,
          }
        : {
            ...Object.fromEntries(
              Object.entries(historySettings).map(([key, value]) => [
                key,
                !!value,
              ])
            ),
            id: historySettings.id,
            snapshot_id: historySettings.snapshot_id,
            record_interval: historySettings.record_interval,
            polling_record_timer_value:
              historySettings.polling_record_timer_value,
            polling_record_timer_unit:
              historySettings?.polling_record_timer_unit || 'minutes',
            recording_locked: !!snapshot?.recording_locked,
            continuous_mode_end_time: historySettings.continuous_mode_end_time,
          };
    reset(initialSettings);
  }, [reset, historySettings, snapshot?.recording_locked, snapshot]);

  // If the user's form says the recording settings are unlocked, unlock them before
  //  processing the other changes

  const onSubmit = async () => {
    const form = getValues();
    if (isAdmin) await updateLock({ ...form, recording_locked: false });
    if (historySettings && 'snapshot_id' in form) {
      await updateSnapshotHistorySettingsMutation.mutateAsync({
        data: form,
        snapshotId: selectedSnapshotId,
      });
    } else {
      await createSnapshotHistorySettingsMutation.mutateAsync({
        data: {
          ...form,
          snapshot_id: selectedSnapshotId,
        },
        snapshotId: selectedSnapshotId,
      });
    }
    if (isAdmin && form.recording_locked) {
      await updateLock(form);
    }
  };

  const [savingToAllSnapshots, setSavingToAllSnapshots] = useState(false);

  const onSubmitToAllSnapshots = async () => {
    setSavingToAllSnapshots(true);
    const form = getValues();
    const snapshotIdArray = snapshots.map((snapshot) => snapshot.id);

    if (isAdmin) {
      await updateAllLocks({ ...form, recording_locked: false });
    }
    const historySettingsUpdate = {
      snapshotIds: snapshotIdArray,
      settings: form,
    };

    await updateMultipleHistorySettingsMutation.mutateAsync({
      data: historySettingsUpdate,
    });
    if (isAdmin && form.recording_locked) {
      await updateAllLocks(form);
    }

    setSavingToAllSnapshots(false);
    setAppliedToAll(selectedSnapshotId);
  };

  return (
    <Grid container direction='column'>
      <Grid
        container
        item
        spacing={2}
        style={{ paddingBottom: '1rem', margin: 0 }}
        wrap='wrap'
      >
        <Grid item xs={6} style={{ minWidth: '15rem' }}>
          <FormControl component='fieldset'>
            <FormLabel component='legend'>Live Mode Recording</FormLabel>
            <FormGroup>
              <Controller
                name='interval_mode'
                control={control}
                rules={{ required: false }}
                render={({ field: { onChange, value } }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={value}
                        name='interval_mode'
                        onChange={onChange}
                        disabled={selectedSnapshotId === -1 || isSnapshotLocked}
                      />
                    }
                    label='Interval'
                  />
                )}
              />
              <Controller
                name='record_interval'
                control={control}
                rules={{ required: false }}
                render={({ field: { onChange, value } }) => (
                  <FormControl>
                    <InputLabel htmlFor='record_interval-input'>
                      Trace Interval
                    </InputLabel>
                    <Input
                      id='record_interval-input'
                      aria-describedby='record_interval-helper-text'
                      value={value}
                      onChange={onChange}
                      name='record_interval'
                      disabled={
                        !watch('interval_mode') ||
                        selectedSnapshotId === -1 ||
                        isSnapshotLocked
                      }
                    />
                    <FormHelperText id='interval-helper-text'>
                      (20 traces is equal to about one second)
                    </FormHelperText>
                  </FormControl>
                )}
              />

              <Controller
                name='interference_mode'
                control={control}
                rules={{
                  required: false,
                  validate: (value) => !(value && watch('continuous_mode')),
                }}
                render={({ field: { onChange, value } }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={value}
                        name='interference_mode'
                        onChange={(e) => {
                          onChange(e);
                          if (!value) setValue('continuous_mode', false);
                        }}
                        disabled={selectedSnapshotId === -1 || isSnapshotLocked}
                      />
                    }
                    label='Record on Alarm'
                  />
                )}
              />
            </FormGroup>
          </FormControl>
        </Grid>
        <Grid item xs={6} style={{ minWidth: '15rem' }}>
          <FormControl component='fieldset'>
            <FormLabel component='legend'>
              Polling / Timeshare Mode Recording
            </FormLabel>
            <Controller
              name='polling_continuous_mode'
              control={control}
              rules={{
                required: false,
                validate: (value) =>
                  !(value && watch('polling_interference_mode')),
              }}
              render={({ field: { onChange, value } }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={value}
                      name='polling_continuous_mode'
                      onChange={(e) => {
                        onChange(e);
                        if (!value) {
                          setValue('polling_interference_mode', false);
                          setValue('polling_record_timer_enabled', false);
                        }
                      }}
                      disabled={selectedSnapshotId === -1 || isSnapshotLocked}
                    />
                  }
                  label='Continuous'
                />
              )}
            />
            <Controller
              name='polling_interference_mode'
              control={control}
              rules={{
                required: false,
                validate: (value) =>
                  !(value && watch('polling_continuous_mode')),
              }}
              render={({ field: { onChange, value } }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={value}
                      name='polling_interference_mode'
                      onChange={(e) => {
                        onChange(e);
                        if (!value) setValue('polling_continuous_mode', false);
                      }}
                      disabled={selectedSnapshotId === -1 || isSnapshotLocked}
                    />
                  }
                  label='Record on Alarm'
                />
              )}
            />
          </FormControl>
          <FormControl>
            <Controller
              name='polling_record_timer_enabled'
              control={control}
              rules={{
                required: false,
                validate: (value) =>
                  !(value && watch('polling_continuous_mode')),
              }}
              render={({ field: { onChange, value } }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={value}
                      name='polling_record_timer_enabled'
                      onChange={(e) => {
                        onChange(e);
                        if (!value) setValue('polling_continuous_mode', false);
                      }}
                      disabled={selectedSnapshotId === -1 || isSnapshotLocked}
                    />
                  }
                  label='Record One Trace Every'
                />
              )}
            />
          </FormControl>
          <Controller
            name='polling_record_timer_value'
            control={control}
            rules={{
              required: false,
              pattern: /^[0-9]+$/,
            }}
            render={({ field: { onChange, value } }) => (
              <FormControl>
                <Input
                  id='polling_record_timer_value-input'
                  aria-describedby='polling_record_timer_value-helper-text'
                  value={value}
                  onKeyDown={(e) => {
                    if (e.key === '.' || e.key === '-') e.preventDefault();
                  }}
                  onChange={onChange}
                  name='polling_record_timer_value'
                  disabled={
                    !watch('polling_record_timer_enabled') ||
                    selectedSnapshotId === -1 ||
                    isSnapshotLocked
                  }
                  type='number'
                />
              </FormControl>
            )}
          />
          <Controller
            name='polling_record_timer_unit'
            control={control}
            rules={{ required: false }}
            render={({ field: { onChange, value } }) => (
              <FormControl variant='standard'>
                <Select
                  id='polling_record_timer_unit'
                  value={value}
                  label='Record Timer Unit'
                  onChange={onChange}
                  disabled={
                    !watch('polling_record_timer_enabled') ||
                    selectedSnapshotId === -1 ||
                    isSnapshotLocked
                  }
                >
                  <MenuItem value={'seconds'}>Second(s)</MenuItem>
                  <MenuItem value={'minutes'}>Minute(s)</MenuItem>
                  <MenuItem value={'hours'}>Hour(s)</MenuItem>
                </Select>
              </FormControl>
            )}
          />
        </Grid>
        <Grid item xs={12} style={{ minWidth: '15rem' }}>
          <FormControl component='fieldset'>
            <FormLabel component='legend'>Recording Lock</FormLabel>
            <Controller
              name='recording_locked'
              control={control}
              rules={{
                required: false,
              }}
              render={({ field: { onChange, value } }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={value}
                      name='recording_locked'
                      onChange={(e) => onChange(e)}
                      disabled={selectedSnapshotId === -1 || !isAdmin}
                    />
                  }
                  label='Recording Settings Locked'
                />
              )}
            />
          </FormControl>
        </Grid>
        <Grid item container spacing={1} alignItems='center'>
          <Grid item>
            <Button
              type='submit'
              variant='contained'
              color='primary'
              onClick={() => handleSubmit(onSubmit)()}
              disabled={selectedSnapshotId === -1 || !isDirty}
            >
              Apply
            </Button>
          </Grid>
          <Grid item>
            <LoadingButton
              type='submit'
              variant='contained'
              color='primary'
              loading={savingToAllSnapshots}
              loadingPosition='start'
              startIcon={getSubmitAllIcon(
                selectedSnapshotId,
                appliedToAll,
                isDirty,
                savingToAllSnapshots
              )}
              onClick={() => handleSubmit(onSubmitToAllSnapshots)()}
              disabled={isSubmitAllDisabled(
                selectedSnapshotId,
                appliedToAll,
                isDirty,
                isAdmin,
                isSnapshotLocked
              )}
            >
              Apply to All Snapshots
            </LoadingButton>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default SnapshotHistorySettingsPicker;
